/*
 * Created on Tue Jul 30 2019
 * Author: Val Liu
 * Copyright (c) 2019
 */

import { Vue } from "vue-property-decorator";
import Server from "@/config/default/server";
import serverConfig from "@/config/default/serverConfig";
import { filter } from "rxjs/operators";
import { FindHome, FindLoginRouter } from "./../../router";
import { BehaviorSubject } from "rxjs";
import data from "@/package.json";
const kAccessToken = `${data.name}:accessToken`;
export const kRememberMe = `${data.name}:rememberMe`;
import { IRouterAuth, PermissionHelper } from "@root/core/utilities/permission-helper";
import { IQuery } from "@/../core/server/restful";

interface IPermissionData {
    Post?: boolean;
    Patch?: boolean;
    Put?: boolean;
    Delete?: boolean;
}

export const DEFAULT_LOGIN_PATH: string = "/auth/login";
export function getLoginPath() {
    let router = FindLoginRouter();
    return serverConfig.loginPath ?? DEFAULT_LOGIN_PATH;
}

declare module "vue/types/vue" {
    export interface Vue {
        $server: Server;
        /// the logined user data.
        $user: any;
        /// the user permission from /apis.
        $permissions: Record</* uri: */ string, IPermissionData>;
        /// permission change version for reactivity.
        $permissionsVersion: number;
        $login: (auth?: ILogin, query?: IQuery) => any;
        $logout: (logoutPath?: string) => void;
        $home: () => void;
        /// check the permission with `uri` and `protocol`.
        // refering to the `$permissions` table.
        $auth: (uri: string | string[], proto?: string) => boolean;
    }
}

interface ILogin {
    username?: string;
    password: string;
    accessToken?: string;
    rememberMe?: boolean;
}

const normalizeRoles = (payload: Record<string, any>): string[] => {
    const directRoles = payload?.roles;
    if (Array.isArray(directRoles)) return directRoles as string[];

    const singleRole = payload?.role;
    if (typeof singleRole === "string" && singleRole.length > 0) {
        return [singleRole];
    }

    const roles: string[] = [];
    for (let i = 1; ; i++) {
        const role = payload[`role${i}`];
        if (role == undefined) break;
        roles.push(role);
    }
    return roles;
};

export const AuthPluginData = Vue.observable({
    server: Server.getDefault(),
    user: {},
    permissions: {},
    permissionsVersion: 0,
});

export const sjPermissions: BehaviorSubject<any> = new BehaviorSubject(null);

export const AuthPlugin = {
    install: function (Vue: any) {
        if (Vue._val_auth_installed) return;
        Vue._val_auth_installed = true;

        Vue.mixin({
            data: function () {
                return {
                    $server: AuthPluginData.server,
                    $user: AuthPluginData.user,
                    $permissions: AuthPluginData.permissions,
                };
            },

            computed: {
                $permissionsVersion(this: Vue) {
                    return AuthPluginData.permissionsVersion;
                },
            },

            methods: {
                $logout: async function (this: Vue, logoutPath?: string) {
                    /// Val: framework v3 no need to call server logout

                    /// do web clean
                    /// 2) clean auth
                    /// clean user
                    Object.keys(AuthPluginData.user as any).forEach((key) => {
                        Vue.delete(AuthPluginData.user, key);
                    });
                    /// assign permissions
                    Object.keys(AuthPluginData.permissions as any).forEach((key) => {
                        Vue.delete(AuthPluginData.permissions, key);
                    });
                    AuthPluginData.permissionsVersion += 1;
                    sjPermissions.next(AuthPluginData.permissions);

                    /// 3) clean local storage
                    localStorage.removeItem(kAccessToken);
                    sessionStorage.removeItem(kAccessToken);
                    localStorage.removeItem(kRememberMe);
                },

                /// /login happens here
                $login: async function (this: Vue, auth?: ILogin, query?: IQuery) {
                    let loginPath = getLoginPath();
                    let storedAccessToken =
                        localStorage.getItem(kAccessToken) ??
                        sessionStorage.getItem(kAccessToken);
                    if (!auth && !storedAccessToken) throw "accessTokenNotExists";
                    if (!auth) auth = { accessToken: storedAccessToken } as any;
                    const rememberMe = (auth as any)?.rememberMe;
                    if ("rememberMe" in (auth as any)) {
                        delete (auth as any).rememberMe;
                    }

                    /// 1) request login
                    let data: any;
                    try {
                        //data = await this.$server.C(loginPath, { input: auth }, cfg);
                        data = await this.$server.useQuery(
                            query,
                            this.$server.C,
                            loginPath as any,
                            { input: auth },
                        );
                    } catch (e) {
                        /// if session invalid, delete it
                        if ((auth as any).accessToken) {
                            localStorage.removeItem(kAccessToken);
                            sessionStorage.removeItem(kAccessToken);
                        }
                        throw e;
                    }
                    let { accessToken } = data;

                    /// 2) request APIs
                    let apis: any = await this.$server.useQuery(
                        query,
                        this.$server.R,
                        "/auth/apis",
                        {
                            accessToken,
                        },
                    );

                    /// 3) assign
                    /// normalize roles from roles/role/role1..roleN
                    data["roles"] = normalizeRoles(data);

                    /// assign user
                    Object.keys(AuthPluginData.user as any).forEach((k) =>
                        Vue.delete(AuthPluginData.user, k),
                    );
                    Object.keys(data ?? {}).forEach((k) => {
                        Vue.set(AuthPluginData.user as any, k, (data as any)[k]);
                    });
                    /// assign permissions (use Vue.set for reactivity)
                    Object.keys(AuthPluginData.permissions as any).forEach(
                        (k) => delete AuthPluginData.permissions?.[k],
                    );
                    Object.keys(apis.APIs ?? {}).forEach((k) => {
                        Vue.set(AuthPluginData.permissions as any, k, apis.APIs[k]);
                    });
                    AuthPluginData.permissionsVersion += 1;
                    sjPermissions.next(AuthPluginData.permissions);
                    /// store accessToken (respect rememberMe)
                    const rememberFlag =
                        typeof rememberMe === "boolean"
                            ? rememberMe
                            : localStorage.getItem(kRememberMe) === "true";
                    localStorage.setItem(kRememberMe, rememberFlag ? "true" : "false");
                    if (rememberFlag) {
                        localStorage.setItem(kAccessToken, accessToken);
                        sessionStorage.removeItem(kAccessToken);
                    } else {
                        localStorage.removeItem(kAccessToken);
                        sessionStorage.setItem(kAccessToken, accessToken);
                    }

                    return data;
                },

                $home: function (this: Vue) {
                    let home = FindHome();
                    this.$router.push(home.path);
                },

                $auth: function (this: Vue, config: IRouterAuth): boolean {
                    return PermissionHelper.authRouter.call(this, config);
                },
            },

            created() {
                this.$server = this.$data.$server;
                this.$user = this.$data.$user;
                this.$permissions = this.$data.$permissions;
            },
        });
    },
};
